/**
 * API Client Service for backend communication
 *
 * Handles all HTTP requests to the backend API with error handling
 * and response transformation.
 */

import axios, { AxiosError } from 'axios';
import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectListResponse,
  ProjectFilters,
} from '../types/project';
import {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentListResponse,
  AgentFilters,
} from '../types/agent';
import {
  Batch,
  BatchCreate,
  BatchCSVUpload,
  BatchUpdate,
  BatchListResponse,
  BatchFilters,
  BatchAnalytics,
  BatchRawInput,
  BatchResults,
  ParseAllResponse,
  CancelParsingResponse,
  UpdateParsingCodeRequest,
  UpdateParsingCodeResponse,
  StartExecutionRequest,
  StartExecutionResponse,
  CancelExecutionResponse,
  UpdateExecutionConcurrencyRequest,
  UpdateExecutionConcurrencyResponse,
} from '../types/batch';
import {
  Session,
  SessionListResponse,
  SessionFilters,
} from '../types/session';
import {
  Job,
  JobCreate,
  JobUpdate,
  JobListResponse,
  JobFilters,
  BulkUploadResponse,
} from '../types/job';
import {
  Result,
  ResultFilters,
  ResultListResponse,
  DurationStatistics,
} from '../types/result';

// Base URL from environment variable, default to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout per spec
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Project Service - API client methods for project CRUD operations
 */
export const projectService = {
  /**
   * List projects with optional filtering, search, and pagination
   */
  list: async (filters?: ProjectFilters): Promise<ProjectListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.status) params.status = filters.status;
      if (filters?.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
      if (filters?.search) params.search = filters.search;
      if (filters?.sort_by) params.sort_by = filters.sort_by;
      if (filters?.sort_order) params.sort_order = filters.sort_order;
      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<ProjectListResponse>('/api/projects', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'project');
    }
  },

  /**
   * Get a single project by ID
   */
  get: async (id: string): Promise<Project> => {
    try {
      const response = await apiClient.get<Project>(`/api/projects/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'project');
    }
  },

  /**
   * Create a new project
   */
  create: async (data: ProjectCreate): Promise<Project> => {
    try {
      const response = await apiClient.post<Project>('/api/projects', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'project');
    }
  },

  /**
   * Update an existing project
   */
  update: async (id: string, data: ProjectUpdate): Promise<Project> => {
    try {
      const response = await apiClient.put<Project>(`/api/projects/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'project');
    }
  },

  /**
   * Delete a project
   */
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/projects/${id}`);
    } catch (error) {
      throw handleApiError(error, 'project');
    }
  },
};

/**
 * Agent Service - API client methods for agent CRUD operations
 */
export const agentService = {
  /**
   * List agents with optional filtering and pagination
   */
  list: async (filters?: AgentFilters): Promise<AgentListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.project_id) params.project_id = filters.project_id;
      if (filters?.search) params.search = filters.search;
      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<AgentListResponse>('/api/agents', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'agent');
    }
  },

  /**
   * Get a single agent by ID
   */
  get: async (id: string): Promise<Agent> => {
    try {
      const response = await apiClient.get<Agent>(`/api/agents/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'agent');
    }
  },

  /**
   * Create a new agent
   */
  create: async (data: AgentCreate): Promise<Agent> => {
    try {
      const response = await apiClient.post<Agent>('/api/agents', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'agent');
    }
  },

  /**
   * Update an existing agent (primarily agent_spec)
   */
  update: async (id: string, data: AgentUpdate): Promise<Agent> => {
    try {
      const response = await apiClient.put<Agent>(`/api/agents/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'agent');
    }
  },

  /**
   * Delete an agent
   */
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/agents/${id}`);
    } catch (error) {
      throw handleApiError(error, 'agent');
    }
  },
};

/**
 * Batch Service - API client methods for batch CRUD operations and execution
 */
export const batchService = {
  /**
   * List batches with optional filtering and pagination
   */
  list: async (filters?: BatchFilters): Promise<BatchListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.project_id) params.project_id = filters.project_id;
      if (filters?.status) params.status = filters.status;
      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<BatchListResponse>('/api/batches', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Get a single batch by ID with statistics
   */
  get: async (id: string): Promise<Batch> => {
    try {
      const response = await apiClient.get<Batch>(`/api/batches/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Create a new batch
   */
  create: async (data: BatchCreate): Promise<Batch> => {
    try {
      const response = await apiClient.post<Batch>('/api/batches', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Create a batch from CSV upload with LLM-powered parsing code generation
   */
  createFromCSV: async (data: BatchCSVUpload): Promise<Batch> => {
    try {
      const response = await apiClient.post<Batch>('/api/batches/upload', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Update an existing batch
   */
  update: async (id: string, data: BatchUpdate): Promise<Batch> => {
    try {
      const response = await apiClient.put<Batch>(`/api/batches/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Delete a batch
   */
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/batches/${id}`);
    } catch (error) {
      throw handleApiError(error, 'batch');
    }
  },

  /**
   * Get batch analytics data
   */
  getAnalytics: async (id: string): Promise<BatchAnalytics> => {
    try {
      const response = await apiClient.get<BatchAnalytics>(`/api/batches/${id}/analytics`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch analytics');
    }
  },

  /**
   * Get raw CSV input data for batch
   */
  getRawInput: async (id: string): Promise<BatchRawInput> => {
    try {
      const response = await apiClient.get<BatchRawInput>(`/api/batches/${id}/raw-input`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch raw input');
    }
  },

  /**
   * Get batch results with metrics
   */
  getResults: async (id: string): Promise<BatchResults> => {
    try {
      const response = await apiClient.get<BatchResults>(`/api/batches/${id}/results`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch results');
    }
  },

  /**
   * Trigger parsing for all raw/parse_failed jobs
   */
  parseAllJobs: async (id: string): Promise<ParseAllResponse> => {
    try {
      const response = await apiClient.post<ParseAllResponse>(`/api/batches/${id}/parse-all`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch parsing');
    }
  },

  /**
   * Cancel batch parsing
   */
  cancelParsing: async (id: string): Promise<CancelParsingResponse> => {
    try {
      const response = await apiClient.post<CancelParsingResponse>(`/api/batches/${id}/cancel-parsing`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch parsing cancellation');
    }
  },

  /**
   * Update parsing code for batch
   */
  updateParsingCode: async (id: string, data: UpdateParsingCodeRequest): Promise<UpdateParsingCodeResponse> => {
    try {
      const response = await apiClient.put<UpdateParsingCodeResponse>(`/api/batches/${id}/parsing-code`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'parsing code update');
    }
  },

  /**
   * Parse a single job (synchronous parsing for one job)
   */
  parseJob: async (batchId: string, jobId: string): Promise<void> => {
    try {
      await apiClient.post(`/api/batches/${batchId}/jobs/${jobId}/parse`);
    } catch (error) {
      throw handleApiError(error, 'job parsing');
    }
  },

  /**
   * Add a job entry manually to a batch
   */
  addJob: async (batchId: string, data: { goal: string; website_url: string; input_id?: string; expected_result?: string }): Promise<{ message: string; job_id: string; batch_id: string }> => {
    try {
      const response = await apiClient.post<{ message: string; job_id: string; batch_id: string }>(`/api/batches/${batchId}/add-job`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'add job');
    }
  },

  /**
   * Update parsing code only (without resetting parsed jobs)
   */
  updateParsingCodeOnly: async (id: string, data: UpdateParsingCodeRequest): Promise<Batch> => {
    try {
      const response = await apiClient.put<Batch>(`/api/batches/${id}/parsing-code-only`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'parsing code update');
    }
  },

  /**
   * Regenerate parsing code from updated instructions via LLM
   */
  regenerateParsingCode: async (id: string, data: { parsing_description: string }): Promise<Batch> => {
    try {
      const response = await apiClient.post<Batch>(`/api/batches/${id}/regenerate-parsing-code`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'parsing code regeneration');
    }
  },

  /**
   * Start batch execution for all or selected jobs
   */
  startExecution: async (id: string, data?: StartExecutionRequest): Promise<StartExecutionResponse> => {
    try {
      const response = await apiClient.post<StartExecutionResponse>(`/api/batches/${id}/execute`, data || {});
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch execution');
    }
  },

  /**
   * Start batch execution for jobs filtered by status
   */
  executeByStatus: async (id: string, status: string): Promise<StartExecutionResponse> => {
    try {
      const response = await apiClient.post<StartExecutionResponse>(
        `/api/batches/${id}/execute-by-status?status=${status}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch execution by status');
    }
  },

  /**
   * Start batch execution for jobs filtered by evaluation status
   */
  executeByEvaluation: async (id: string, evaluationStatus: string): Promise<StartExecutionResponse> => {
    try {
      const response = await apiClient.post<StartExecutionResponse>(
        `/api/batches/${id}/execute-by-evaluation?eval=${evaluationStatus}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch execution by evaluation');
    }
  },

  /**
   * Cancel batch execution gracefully
   */
  cancelExecution: async (id: string): Promise<CancelExecutionResponse> => {
    try {
      const response = await apiClient.post<CancelExecutionResponse>(`/api/batches/${id}/cancel-execution`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'batch execution cancellation');
    }
  },

  /**
   * Update execution concurrency during execution
   */
  updateExecutionConcurrency: async (
    id: string,
    data: UpdateExecutionConcurrencyRequest
  ): Promise<UpdateExecutionConcurrencyResponse> => {
    try {
      const response = await apiClient.patch<UpdateExecutionConcurrencyResponse>(
        `/api/batches/${id}/execution-concurrency`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'execution concurrency update');
    }
  },
};

/**
 * Job Service - API client methods for job CRUD operations and execution
 */
export const jobService = {
  /**
   * List jobs with optional filtering, sorting, search, and pagination
   */
  list: async (filters?: JobFilters): Promise<JobListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.status) params.status = filters.status;
      if (filters?.eval) params.eval = filters.eval;
      if (filters?.date_from) params.date_from = filters.date_from;
      if (filters?.date_to) params.date_to = filters.date_to;
      if (filters?.search) params.search = filters.search;
      if (filters?.sort_by) params.sort_by = filters.sort_by;
      if (filters?.sort_order) params.sort_order = filters.sort_order;
      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      // Use batch-specific endpoint if batch_id is provided (supports eval filter)
      if (filters?.batch_id) {
        const response = await apiClient.get<JobListResponse>(`/api/batches/${filters.batch_id}/jobs`, { params });
        return response.data;
      }

      // Fall back to general jobs endpoint
      const response = await apiClient.get<JobListResponse>('/api/jobs', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Get a single job by ID
   */
  get: async (id: string): Promise<Job> => {
    try {
      const response = await apiClient.get<Job>(`/api/jobs/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Create a new job (with implicit agent creation if agent_id not provided)
   */
  create: async (data: JobCreate): Promise<Job> => {
    try {
      const response = await apiClient.post<Job>('/api/jobs', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Update an existing job
   */
  update: async (id: string, data: JobUpdate): Promise<Job> => {
    try {
      const response = await apiClient.put<Job>(`/api/jobs/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Delete a job
   */
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/jobs/${id}`);
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Execute a single job with optional settings overrides
   */
  execute: async (id: string, settings?: {
    browser_type?: string;
    use_proxy?: boolean;
    use_memory_bank?: boolean;
    tinyctx_mode?: string;
    recording_id?: string;
  }): Promise<void> => {
    try {
      await apiClient.post(`/api/jobs/${id}/run`, settings || {});
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Execute all pending jobs in a batch
   */
  batchExecute: async (batchId: string): Promise<void> => {
    try {
      await apiClient.post('/api/jobs/batch-run', null, { params: { batch_id: batchId } });
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Retry a failed job (creates new job with same parameters)
   */
  retry: async (id: string): Promise<Job> => {
    try {
      const response = await apiClient.post<Job>(`/api/jobs/${id}/retry`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },

  /**
   * Reset stuck running jobs to pending for a batch
   */
  resetRunning: async (batchId: string): Promise<{
    message: string;
    batch_id: string;
    reset_count: number;
  }> => {
    try {
      const response = await apiClient.post(`/api/jobs/reset-running?batch_id=${batchId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'reset running jobs');
    }
  },

  /**
   * Bulk upload jobs from CSV/JSON with natural language mapping
   */
  bulkUpload: async (
    batchId: string,
    file: File,
    mappingInstructions: string
  ): Promise<BulkUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping_instructions', mappingInstructions);

      const response = await apiClient.post<BulkUploadResponse>(
        `/api/batches/${batchId}/jobs/bulk-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'job');
    }
  },
};

/**
 * Session Service - API client methods for session operations
 */
export const sessionService = {
  /**
   * Get a single session by ID
   */
  get: async (id: string): Promise<Session> => {
    try {
      const response = await apiClient.get<Session>(`/api/sessions/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'session');
    }
  },

  /**
   * List sessions for a job
   */
  listByJob: async (jobId: string, filters?: SessionFilters): Promise<SessionListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<SessionListResponse>(`/api/sessions/job/${jobId}`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'session');
    }
  },

  /**
   * Get stream URL for a session
   */
  getStreamUrl: (sessionId: string): string => {
    const token = localStorage.getItem('access_token');
    return `${API_BASE_URL}/api/sessions/${sessionId}/stream${token ? `?token=${token}` : ''}`;
  },

  /**
   * Stop a running session
   */
  stop: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.patch(`/api/sessions/${sessionId}`, {
        abort_task: true
      });
    } catch (error) {
      throw handleApiError(error, 'session stop');
    }
  },
};

/**
 * Result Service - API client methods for result operations
 */
export const resultService = {
  /**
   * List results with optional filtering and pagination
   */
  list: async (filters: ResultFilters): Promise<ResultListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters.batch_id) params.batch_id = filters.batch_id;
      if (filters.status) params.status = filters.status;
      if (filters.sort_by) params.sort_by = filters.sort_by;
      if (filters.sort_order) params.sort_order = filters.sort_order;
      if (filters.limit !== undefined) params.limit = filters.limit;
      if (filters.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<ResultListResponse>('/api/results', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'result');
    }
  },

  /**
   * Get a single result by ID
   */
  get: async (id: string): Promise<Result> => {
    try {
      const response = await apiClient.get<Result>(`/api/results/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'result');
    }
  },

  /**
   * Recheck results for a batch based on current critical keys
   */
  recheck: async (batchId: string): Promise<{
    message: string;
    batch_id: string;
    total_rechecked: number;
    status_changes: number;
    new_complete: number;
    new_empty: number;
    new_error: number;
  }> => {
    try {
      const response = await apiClient.post(`/api/results/recheck?batch_id=${batchId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'result recheck');
    }
  },

  /**
   * Reset 'Not Executed' (none) results for a batch
   */
  resetNone: async (batchId: string): Promise<{
    message: string;
    batch_id: string;
    deleted_count: number;
    jobs_reset: number;
  }> => {
    try {
      const response = await apiClient.post(`/api/results/reset-none?batch_id=${batchId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'reset none results');
    }
  },

  /**
   * Get duration statistics for completed jobs in a batch
   */
  getDurationStatistics: async (batchId: string): Promise<DurationStatistics> => {
    try {
      const response = await apiClient.get<DurationStatistics>('/api/results/stats/duration', {
        params: { batch_id: batchId }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'duration statistics');
    }
  },
};

/**
 * API Key types
 */
export interface ApiKeyInfo {
  key_id: string;
  name: string;
  key_prefix: string;
  key_suffix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface CreateApiKeyResponse {
  key_id: string;
  name: string;
  key: string;
  key_prefix: string;
  created_at: string;
}

/**
 * API Key Service - API client methods for API key management
 */
export const apiKeyService = {
  /**
   * List all API keys for the current user
   */
  list: async (): Promise<{ keys: ApiKeyInfo[] }> => {
    try {
      const response = await apiClient.get<{ keys: ApiKeyInfo[] }>('/api/api-keys');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'API key');
    }
  },

  /**
   * Create a new API key
   */
  create: async (name: string): Promise<CreateApiKeyResponse> => {
    try {
      const response = await apiClient.post<CreateApiKeyResponse>('/api/api-keys', { name });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'API key');
    }
  },

  /**
   * Delete an API key
   */
  delete: async (keyId: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/api-keys/${keyId}`);
    } catch (error) {
      throw handleApiError(error, 'API key');
    }
  },
};

/**
 * Handle API errors and transform them into user-friendly messages
 *
 * Per spec: NO retry logic - just show error messages
 */
function handleApiError(error: unknown, entityType: string = 'resource'): Error {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;

    // Network or timeout error
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timed out. Please try again.');
      }
      return new Error('Unable to connect to the server. Please check your connection.');
    }

    // Server returned an error response
    const status = axiosError.response.status;
    const data = axiosError.response.data;

    // Extract error message from response
    const message = data?.detail || data?.message || axiosError.message;

    switch (status) {
      case 400:
        return new Error(`Validation error: ${message}`);
      case 404:
        return new Error(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} not found.`);
      case 409:
        return new Error(message); // Conflict message from backend
      case 500:
        return new Error('An error occurred on the server. Please try again later.');
      default:
        return new Error(message || 'An unexpected error occurred.');
    }
  }

  // Non-axios error
  return error instanceof Error ? error : new Error('An unexpected error occurred.');
}
